/**
 * Provider tree for the unified shell.
 *
 * Assembled with the shared buildProvidersTree composer (@andavo/web-ui/api).
 * Order: StrictMode → QueryClientProvider → BrowserRouter, with a sonner Toaster
 * mounted alongside. (We deliberately do NOT use @azure/msal-react's MsalProvider
 * — MSAL is module-level state managed in src/auth/msal.ts, mirroring experts-web;
 * no React context provider is needed and it keeps the dep set to the approved
 * three. At the andavo-dev merge this is replaced by the real WebProvidersTree
 * and the auth swap stays isolated to src/auth/.)
 *
 * The QueryClient is graduated to a production config (gcTime/retry + toast-on-
 * error via mutation meta). Real APIs land per-module; expense stays mock now.
 */
import { buildProvidersTree } from '@andavo/web-ui/api';
import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { StrictMode, type ReactNode } from 'react';
import { BrowserRouter } from 'react-router';
import { toast } from 'sonner';

/**
 * Production QueryClient config (mirrors andavo-dev / experts-web shape):
 *   - staleTime 30s, gcTime 5m
 *   - retry once (network blips), no refetch-on-focus
 *   - toast-on-error for mutations that opt in via meta.errorMessage
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      // Opt-in: a mutation sets meta.errorMessage to surface a toast on failure.
      const message =
        (mutation.meta?.errorMessage as string | undefined) ??
        (error instanceof Error ? error.message : undefined);
      if (message) toast.error(message);
    },
  }),
});

const QueryProvider = ({ children }: { children: ReactNode }) => (
  <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
);

const ProvidersTree = buildProvidersTree([
  [StrictMode],
  [QueryProvider],
  [BrowserRouter],
]);

export function AppProviders({ children }: { children: ReactNode }) {
  return <ProvidersTree>{children}</ProvidersTree>;
}
